import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Chip,
  CssBaseline,
  FormControl,
  FormHelperText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { axiosClient, importCampaignCsv } from "../AxiosClient";
import Modal from "../modal/Modal";

const colors = {
  primaryDark: "#161E55",
  accentGreen: "#A8C8AF",
  accentGreenSoft: "#BADEC1",
  selectedChipBg: "#e0f0e8",
};

const statusColor = {
  ACTIVO: "#2e7d32",
  PENDIENTE: "#ef6c00",
  MORA: "#c62828",
  CANCELADO: "#455a64",
};

const pageSizeOptions = [10, 20, 30];
const searchEndpoint = "/v1/moonflow/campaigns/credits";
const confirmEndpoint = "/v1/moonflow/campaigns/confirm";

const normalizeRow = (row) => {
  const oldId = String(row?.oldId ?? row?.oldID ?? row?.old_id ?? "");
  const cedula = String(
    row?.cedula ?? row?.documentNumber ?? row?.document ?? row?.idNumber ?? ""
  );
  const computedName =
    row?.name ??
    `${row?.firstName ?? ""} ${row?.lastName ?? ""}`.trim() ??
    row?.clientName ??
    "";

  const creditsRaw = row?.credits ?? row?.activeCredits ?? row?.campaignCredits ?? [];
  const credits = creditsRaw.map((credit) => ({
    id: String(credit?.id ?? credit?.creditId ?? credit?.oldId ?? ""),
    amount: String(credit?.amount ?? credit?.capitalValue ?? credit?.value ?? ""),
    status: String(credit?.status ?? credit?.creditStatus ?? ""),
    date: String(credit?.date ?? credit?.disbursementDate ?? credit?.createdAt ?? ""),
  }));

  return {
    oldId,
    cedula,
    name: computedName,
    credits,
  };
};

const getResponseList = (responseData) => {
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData?.content)) return responseData.content;
  if (Array.isArray(responseData?.items)) return responseData.items;
  if (Array.isArray(responseData?.rows)) return responseData.rows;
  if (Array.isArray(responseData?.data)) return responseData.data;
  return [];
};

const getResponseTotalPages = (responseData, size, rowsLength) => {
  if (typeof responseData?.totalPages === "number") return responseData.totalPages;
  if (typeof responseData?.pages === "number") return responseData.pages;
  if (typeof responseData?.totalPage === "number") return responseData.totalPage;
  if (typeof responseData?.totalElements === "number") {
    return Math.max(1, Math.ceil(responseData.totalElements / size));
  }
  if (typeof responseData?.total === "number") {
    return Math.max(1, Math.ceil(responseData.total / size));
  }
  return Math.max(1, Math.ceil(rowsLength / size));
};

const getResponseTotalElements = (responseData, size, rowsLength) => {
  if (typeof responseData?.totalElements === "number") return responseData.totalElements;
  if (typeof responseData?.total === "number") return responseData.total;
  if (typeof responseData?.count === "number") return responseData.count;
  const pages = getResponseTotalPages(responseData, size, rowsLength);
  return pages * size;
};

const generateCampaignCode = (shortName) => {
  const normalizedShort = shortName
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase()
    .slice(0, 8)
    .padEnd(4, "X");
  const date = new Date();
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const base36Ts = Date.now().toString(36).toUpperCase().slice(-6);
  const randomBlock = Math.random().toString(36).toUpperCase().replace(".", "").slice(2, 6);
  return `CPN${yyyy}${mm}${dd}${normalizedShort}${base36Ts}${randomBlock}`;
};

export default function Campanas() {
  const [searchMode, setSearchMode] = useState("oldId");
  const [searchQuery, setSearchQuery] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [rows, setRows] = useState([]);
  const [selectedCredits, setSelectedCredits] = useState({});
  const [shortName, setShortName] = useState("");
  const [campaignCode, setCampaignCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isImportingCsv, setIsImportingCsv] = useState(false);
  const [importShortName, setImportShortName] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [importErrors, setImportErrors] = useState({
    shortName: "",
    file: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const normalizedQuery = searchQuery.trim().toLowerCase();

  useEffect(() => {
    const timer = setTimeout(() => {
      const fetchCampaignCredits = async () => {
        setIsLoading(true);
        const trimmedQuery = searchQuery.trim();
        const shouldSendFilterParams = Boolean(trimmedQuery);
        const shouldSendPagingParams =
          shouldSendFilterParams || page !== 0 || pageSize !== 10;
        const requestParams = {};

        if (shouldSendFilterParams) {
          requestParams.mode = searchMode;
          requestParams.query = trimmedQuery;
        }
        if (shouldSendPagingParams) {
          requestParams.page = page;
          requestParams.size = pageSize;
        }

        try {
          console.log("[Campanas][GET] Request", {
            endpoint: searchEndpoint,
            params: Object.keys(requestParams).length ? requestParams : null,
          });
          const response = await axiosClient.get(searchEndpoint, {
            params: Object.keys(requestParams).length ? requestParams : undefined,
          });
          const responseData = response?.data;
          const list = getResponseList(responseData).map((item) => normalizeRow(item));
          console.log("[Campanas][GET] Response", {
            status: response?.status,
            rows: list.length,
            totalPages: getResponseTotalPages(responseData, pageSize, list.length),
            totalElements: getResponseTotalElements(responseData, pageSize, list.length),
            raw: responseData,
          });
          setRows(list);
          setTotalElements(getResponseTotalElements(responseData, pageSize, list.length));
        } catch (error) {
          console.error("[Campanas][GET] Error", {
            status: error?.response?.status,
            data: error?.response?.data,
            message: error?.message,
          });
          setRows([]);
          setTotalElements(0);
          setSnackbar({
            open: true,
            message:
              error?.response?.data?.message ||
              "No fue posible cargar campañas con ese filtro.",
            severity: "error",
          });
        } finally {
          setIsLoading(false);
        }
      };

      fetchCampaignCredits();
    }, 250);

    return () => clearTimeout(timer);
  }, [searchMode, searchQuery, page, pageSize]);

  const rowsInPage = useMemo(() => rows, [rows]);

  const placeholder =
    searchMode === "oldId" ? "Buscar por Old ID" : "Buscar por Cédula";

  const selectedCreditsArray = useMemo(() => Object.values(selectedCredits), [selectedCredits]);

  const isSelected = (creditUniqueId) => Boolean(selectedCredits[creditUniqueId]);

  const isRowSelected = (row) => {
    const rowCredits = row?.credits || [];
    if (rowCredits.length === 0) return false;
    return rowCredits.every((credit) => isSelected(`${row.oldId}-${credit.id}`));
  };

  const isRowIndeterminate = (row) => {
    const rowCredits = row?.credits || [];
    if (rowCredits.length === 0) return false;
    const selectedCount = rowCredits.filter((credit) =>
      isSelected(`${row.oldId}-${credit.id}`)
    ).length;
    return selectedCount > 0 && selectedCount < rowCredits.length;
  };

  const toggleCreditSelection = (creditUniqueId, payload) => {
    setSelectedCredits((currentSelected) => {
      const nextSelected = { ...currentSelected };
      if (nextSelected[creditUniqueId]) {
        delete nextSelected[creditUniqueId];
        return nextSelected;
      }
      nextSelected[creditUniqueId] = payload;
      return nextSelected;
    });
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setPage(0);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 0) {
      return;
    }
    setPage(newPage);
  };

  const handleRowCheckboxChange = (row, checked) => {
    setSelectedCredits((currentSelected) => {
      const nextSelected = { ...currentSelected };
      (row?.credits || []).forEach((credit) => {
        const uniqueCreditId = `${row.oldId}-${credit.id}`;
        if (checked) {
          nextSelected[uniqueCreditId] = {
            creditId: credit.id,
            oldId: row.oldId,
            cedula: row.cedula,
            clientName: row.name,
            amount: credit.amount,
            status: credit.status,
            date: credit.date,
          };
        } else {
          delete nextSelected[uniqueCreditId];
        }
      });
      return nextSelected;
    });
  };

  const handleConfirmSelection = async () => {
    if (!shortName.trim()) {
      setSnackbar({
        open: true,
        message: "Debes ingresar un nombre corto para la campaña.",
        severity: "warning",
      });
      return;
    }

    const generatedCode = generateCampaignCode(shortName);
    const groupedByClient = selectedCreditsArray.reduce((accumulator, credit) => {
      const documentNumber = String(credit.cedula ?? "").trim();
      const creditIdNumber = Number(credit.creditId);
      if (!documentNumber) {
        return accumulator;
      }
      if (!Number.isInteger(creditIdNumber)) {
        return accumulator;
      }

      if (!accumulator[documentNumber]) {
        accumulator[documentNumber] = [];
      }
      accumulator[documentNumber].push(creditIdNumber);
      return accumulator;
    }, {});

    const clients = Object.entries(groupedByClient).map(([documentNumber, creditIds]) => ({
      documentNumber,
      clientCampaignValue: `${generatedCode}${documentNumber.slice(-4)}`,
      selectedCreditIds: creditIds,
    }));

    if (clients.length === 0) {
      setSnackbar({
        open: true,
        message: "Los créditos seleccionados no tienen IDs numéricos válidos.",
        severity: "warning",
      });
      return;
    }

    const payload = {
      shortName: shortName.trim(),
      defaultCampaignValue: generatedCode,
      clients,
    };

    setIsConfirming(true);
    try {
      console.log("[Campanas][POST] Request", {
        endpoint: confirmEndpoint,
        payload,
      });
      const response = await axiosClient.post(confirmEndpoint, payload);
      console.log("[Campanas][POST] Response", {
        status: response?.status,
        data: response?.data,
      });
      const returnedCode =
        response?.data?.defaultCampaignValue ??
        response?.data?.campaignCode ??
        response?.data?.code ??
        response?.data?.value ??
        generatedCode;
      setCampaignCode(returnedCode);
      setSelectedCredits({});
      setShortName("");
      setSnackbar({
        open: true,
        message: "Campaña confirmada exitosamente.",
        severity: "success",
      });
    } catch (error) {
      console.error("[Campanas][POST] Error", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "No fue posible confirmar la selección.",
        severity: "error",
      });
    } finally {
      setIsConfirming(false);
    }
  };

  const getDownloadFileName = (contentDisposition) => {
    if (!contentDisposition) {
      return "campaign_clients_analysis.xlsx";
    }

    const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const filenameMatch = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
    if (filenameMatch?.[1]) {
      return filenameMatch[1];
    }

    return "campaign_clients_analysis.xlsx";
  };

  const downloadBlob = (blob, filename) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  const getImportErrorMessage = async (error) => {
    const responseData = error?.response?.data;
    if (responseData instanceof Blob) {
      try {
        const text = await responseData.text();
        if (!text) return "No fue posible importar el archivo CSV.";
        try {
          const parsed = JSON.parse(text);
          return parsed?.message || parsed?.error || text;
        } catch (parseError) {
          return text;
        }
      } catch (blobError) {
        return "No fue posible importar el archivo CSV.";
      }
    }

    if (typeof responseData === "string" && responseData.trim()) {
      return responseData;
    }

    if (responseData?.message) {
      return responseData.message;
    }

    return "No fue posible importar el archivo CSV.";
  };

  const handleOpenImportModal = () => {
    setImportErrors({ shortName: "", file: "" });
    setIsImportModalOpen(true);
  };

  const handleCloseImportModal = () => {
    if (isImportingCsv) return;
    setIsImportModalOpen(false);
    setImportShortName("");
    setImportFile(null);
    setImportErrors({ shortName: "", file: "" });
  };

  const handleImportCsv = async (event) => {
    event.preventDefault();
    const shortNameError = importShortName.trim() ? "" : "Nombre corto es obligatorio";
    const fileError = importFile ? "" : "Debes seleccionar un archivo CSV";

    if (shortNameError || fileError) {
      setImportErrors({
        shortName: shortNameError,
        file: fileError,
      });
      return;
    }

    const userUuid = localStorage.getItem("userUuid");
    if (!userUuid) {
      setSnackbar({
        open: true,
        message: "No se encontró userUuid en sesión.",
        severity: "error",
      });
      return;
    }

    setIsImportingCsv(true);
    try {
      console.log("[Campanas][IMPORT] Request", {
        endpoint: `/v1/moonflow/campaigns/import/${userUuid}`,
        shortName: importShortName.trim(),
        fileName: importFile?.name,
      });

      const response = await importCampaignCsv(userUuid, importShortName.trim(), importFile);
      console.log("[Campanas][IMPORT] Response", {
        status: response?.status,
        headers: response?.headers,
      });

      const fileName = getDownloadFileName(response?.headers?.["content-disposition"]);
      downloadBlob(response.data, fileName);
      handleCloseImportModal();
      setSnackbar({
        open: true,
        message: "Archivo procesado y descargado exitosamente.",
        severity: "success",
      });
    } catch (error) {
      const importErrorMessage = await getImportErrorMessage(error);
      console.error("[Campanas][IMPORT] Error", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
      setSnackbar({
        open: true,
        message: importErrorMessage,
        severity: "error",
      });
    } finally {
      setIsImportingCsv(false);
    }
  };

  return (
    <React.Fragment>
      <CssBaseline />
      <Toolbar />
      <Box sx={{ width: "100%", margin: "18px 14px 90px 14px", paddingTop: "80px" }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: `1px solid ${colors.accentGreenSoft}`,
            mt: 2,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Box
              sx={{
                display: "inline-flex",
                borderRadius: "999px",
                border: `1px solid ${colors.primaryDark}`,
                p: "3px",
                backgroundColor: "#ffffff",
              }}
            >
              {[
                { key: "oldId", label: "Old ID" },
                { key: "cedula", label: "Cédula" },
              ].map((option) => (
                <Button
                  key={option.key}
                  onClick={() => setSearchMode(option.key)}
                  sx={{
                    borderRadius: "999px",
                    px: 2.5,
                    py: 0.5,
                    minWidth: 110,
                    color:
                      searchMode === option.key ? colors.primaryDark : "text.primary",
                    backgroundColor:
                      searchMode === option.key ? colors.accentGreen : "transparent",
                    "&:hover": {
                      backgroundColor:
                        searchMode === option.key
                          ? colors.accentGreen
                          : colors.accentGreenSoft,
                    },
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </Box>

            <TextField
              fullWidth
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value);
                setPage(0);
              }}
              placeholder={placeholder}
              size="small"
              sx={{ maxWidth: 500 }}
            />

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">Filas por página</Typography>
              <Select size="small" value={pageSize} onChange={handlePageSizeChange}>
                {pageSizeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              <Button
                variant="contained"
                onClick={handleOpenImportModal}
                sx={{
                  backgroundColor: colors.primaryDark,
                  "&:hover": {
                    backgroundColor: colors.primaryDark,
                  },
                }}
              >
                Importar CSV
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <TableContainer
          component={Paper}
          sx={{ mt: 2, borderRadius: 3, border: `1px solid ${colors.accentGreenSoft}` }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: colors.primaryDark }}>
                <TableCell sx={{ color: colors.accentGreen, fontWeight: 700 }}>
                  Select
                </TableCell>
                <TableCell sx={{ color: colors.accentGreen, fontWeight: 700 }}>
                  Document number
                </TableCell>
                <TableCell sx={{ color: colors.accentGreen, fontWeight: 700 }}>
                  Client name
                </TableCell>
                <TableCell sx={{ color: colors.accentGreen, fontWeight: 700 }}>
                  Active credits
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                      <CircularProgress size={22} />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {rowsInPage.map((row) => {
                const shouldHighlight =
                  normalizedQuery.length > 0 &&
                  String(row[searchMode]).toLowerCase().includes(normalizedQuery);
                return (
                  <TableRow
                    key={`${row.oldId}-${row.cedula}`}
                    sx={{
                      backgroundColor: shouldHighlight
                        ? colors.accentGreenSoft
                        : "transparent",
                    }}
                  >
                    <TableCell>
                      <Checkbox
                        checked={isRowSelected(row)}
                        indeterminate={isRowIndeterminate(row)}
                        onChange={(event) =>
                          handleRowCheckboxChange(row, event.target.checked)
                        }
                      />
                    </TableCell>
                    <TableCell>{row.cedula}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>
                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        {(row.credits || []).map((credit) => {
                          const uniqueCreditId = `${row.oldId}-${credit.id}`;
                          const selected = isSelected(uniqueCreditId);
                          return (
                            <Box
                              key={uniqueCreditId}
                              onClick={() =>
                                toggleCreditSelection(uniqueCreditId, {
                                  creditId: credit.id,
                                  oldId: row.oldId,
                                  cedula: row.cedula,
                                  clientName: row.name,
                                  amount: credit.amount,
                                  status: credit.status,
                                  date: credit.date,
                                })
                              }
                              sx={{
                                cursor: "pointer",
                                border: `1px solid ${
                                  selected ? colors.primaryDark : "rgba(22,30,85,0.2)"
                                }`,
                                backgroundColor: selected
                                  ? colors.selectedChipBg
                                  : "rgba(186,222,193,0.2)",
                                borderRadius: 2,
                                p: 1.2,
                                minWidth: 210,
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  borderColor: colors.primaryDark,
                                },
                              }}
                            >
                              <Stack direction="row" justifyContent="space-between" gap={1}>
                                <Typography variant="body2" fontWeight={700}>
                                  ID: {credit.id}
                                </Typography>
                                {selected && (
                                  <CheckCircleRoundedIcon
                                    sx={{ color: colors.primaryDark, fontSize: 18 }}
                                  />
                                )}
                              </Stack>
                              <Typography variant="body2">{credit.amount}</Typography>
                              <Stack direction="row" justifyContent="space-between" mt={0.5}>
                                <Chip
                                  size="small"
                                  label={credit.status}
                                  sx={{
                                    fontWeight: 600,
                                    color: "#fff",
                                    backgroundColor:
                                      statusColor[credit.status] || colors.primaryDark,
                                  }}
                                />
                                <Typography variant="caption">{credit.date}</Typography>
                              </Stack>
                            </Box>
                          );
                        })}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {rowsInPage.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography sx={{ py: 2, textAlign: "center" }}>
                      No se encontraron resultados para la búsqueda.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={pageSizeOptions}
            component="div"
            count={totalElements}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={(event, newPage) => handlePageChange(newPage)}
            onRowsPerPageChange={(event) => {
              setPageSize(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      </Box>

      {selectedCreditsArray.length > 0 && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 240,
            width: "calc(100% - 240px)",
            backgroundColor: colors.primaryDark,
            color: "#fff",
            py: 1.5,
            px: 3,
            zIndex: 1000,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography sx={{ minWidth: 180 }}>
            {selectedCreditsArray.length} crédito(s) seleccionados
          </Typography>
          <TextField
            value={shortName}
            onChange={(event) => setShortName(event.target.value)}
            placeholder="Nombre corto campaña"
            size="small"
            sx={{
              backgroundColor: "#fff",
              borderRadius: 1,
              minWidth: 260,
            }}
          />
          <Button
            variant="contained"
            onClick={handleConfirmSelection}
            disabled={isConfirming}
            sx={{
              backgroundColor: colors.accentGreen,
              color: colors.primaryDark,
              fontWeight: 700,
              "&:hover": {
                backgroundColor: colors.accentGreenSoft,
              },
            }}
          >
            {isConfirming ? "Confirmando..." : "Confirm selection"}
          </Button>
        </Box>
      )}

      {campaignCode && (
        <Box sx={{ position: "fixed", right: 20, bottom: 90, zIndex: 1001 }}>
          <Paper
            sx={{
              p: 1.2,
              border: `1px solid ${colors.accentGreenSoft}`,
              borderRadius: 2,
            }}
          >
            <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
              Código campaña
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              {campaignCode}
            </Typography>
          </Paper>
        </Box>
      )}

      {isImportModalOpen && (
        <Modal activeModal={isImportModalOpen} handleModal={handleCloseImportModal}>
          <Box
            sx={{
              width: "86%",
              margin: "1.2rem auto 2rem auto",
              padding: "1.4rem",
              borderRadius: "14px",
              border: `1px solid ${colors.accentGreenSoft}`,
              backgroundColor: "#f9fcfa",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                textAlign: "center",
                color: colors.primaryDark,
                fontWeight: 700,
                mb: 0.5,
              }}
            >
              Importar CSV de Campaña
            </Typography>
            <Typography
              variant="body2"
              sx={{ textAlign: "center", color: "text.secondary", mb: 2.5 }}
            >
              Carga el archivo con clientes para procesar la campaña.
            </Typography>
            <form onSubmit={handleImportCsv}>
              <Box mt={3}>
                <FormControl fullWidth error={Boolean(importErrors.shortName)}>
                  <TextField
                    id="campaign-short-name"
                    label="Nombre corto campaña"
                    value={importShortName}
                    onChange={(event) =>
                      setImportShortName(event.target.value.toUpperCase())
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "#fff",
                      },
                    }}
                  />
                  {importErrors.shortName && (
                    <FormHelperText>{importErrors.shortName}</FormHelperText>
                  )}
                </FormControl>
              </Box>
              <Box mt={3}>
                <FormControl fullWidth error={Boolean(importErrors.file)}>
                  <Button
                    variant="outlined"
                    component="label"
                    sx={{
                      justifyContent: "center",
                      textTransform: "none",
                      borderStyle: "dashed",
                      borderWidth: "2px",
                      borderColor: colors.primaryDark,
                      color: colors.primaryDark,
                      backgroundColor: "#ffffff",
                      height: 58,
                      fontWeight: 600,
                      borderRadius: "12px",
                      transition: "all .2s ease",
                      "&:hover": {
                        borderStyle: "dashed",
                        borderWidth: "2px",
                        borderColor: colors.primaryDark,
                        backgroundColor: "#eef6f0",
                      },
                      "&:focus-visible": {
                        outline: `2px solid ${colors.accentGreen}`,
                        outlineOffset: "2px",
                      },
                    }}
                  >
                    {importFile?.name || "Seleccionar archivo CSV"}
                    <input
                      type="file"
                      hidden
                      accept=".csv,text/csv"
                      onChange={(event) => {
                        const selectedFile = event.target.files?.[0] || null;
                        setImportFile(selectedFile);
                      }}
                    />
                  </Button>
                  {importErrors.file && <FormHelperText>{importErrors.file}</FormHelperText>}
                </FormControl>
              </Box>
              <Stack direction="row" spacing={2} justifyContent="center" mt={4}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleCloseImportModal}
                  disabled={isImportingCsv}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isImportingCsv}
                  sx={{
                    backgroundColor: colors.primaryDark,
                    borderRadius: "10px",
                    px: 2.5,
                    "&:hover": {
                      backgroundColor: colors.primaryDark,
                    },
                  }}
                >
                  {isImportingCsv ? "Cargando..." : "Cargar archivo"}
                </Button>
              </Stack>
            </form>
          </Box>
        </Modal>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}
